import { EditRecordButton } from "@/components/explorer/RecordEditor";
import { useMemo, useState } from "react";
import { PageHeader } from "@/components/shared/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import * as Icons from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { toast } from "sonner";
import { useCollection } from "@/hooks/useCollection";
import { adapter } from "@/services/db";
import { useAuth } from "@/context/AuthContext";
import { eventMeta, TAB_META } from "@/lib/activityMap";
import { text, toDate } from "@/lib/format";

const ago = (t) => { const d = toDate(t); return d ? formatDistanceToNowStrict(d, { addSuffix: true }) : "—"; };
const ts = (l) => l.server_ts || l.created_at;
const metaStr = (l) => (l.description ? text(l.description) : (l.metadata && typeof l.metadata === "object" ? Object.entries(l.metadata).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ") : (l.entity_type ? `${l.entity_type} ${l.entity_id || ""}` : "")));

export default function ActivityFeed() {
  const { user } = useAuth();
  const logs = useCollection("activity_logs");
  const [tab, setTab] = useState("customer");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ app: "ops", change_type: "", description: "", related_files: "" });

  const canApprove = ["admin", "ops_manager"].includes(user?.role);
  const now = () => new Date().toISOString();

  const enriched = useMemo(() =>
    [...logs]
      .map((l) => ({ ...l, _meta: eventMeta(l), _t: toDate(ts(l))?.getTime() || 0 }))
      .sort((a, b) => b._t - a._t)
  , [logs]);

  const counts = useMemo(() => {
    const c = { customer: 0, professional: 0, system: 0 };
    enriched.forEach((l) => { c[l._meta.tab] = (c[l._meta.tab] || 0) + 1; });
    return c;
  }, [enriched]);

  const rows = enriched.filter((l) => l._meta.tab === tab && (!q || JSON.stringify(l).toLowerCase().includes(q.toLowerCase())));

  const review = async (it, status) => {
    try {
      await adapter.update("activity_logs", it.id, { status, reviewed_by: user?.email, reviewed_at: now() });
      toast.success(`Change ${status}`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const logChange = async () => {
    if (!form.change_type) return toast.error("Change type required");
    try {
      await adapter.add("activity_logs", {
        app: form.app, action: form.change_type, change_type: form.change_type, description: form.description,
        related_files: form.related_files ? form.related_files.split(",").map((s) => s.trim()).filter(Boolean) : [],
        status: "pending", changed_by: user?.email, performed_by: user?.email, performed_by_role: user?.role,
        entity_type: "change", created_at: now(), server_ts: now(),
      });
      toast.success("Change logged");
      setForm({ app: "ops", change_type: "", description: "", related_files: "" });
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div>
      <PageHeader title="Activity & Changes" subtitle="Structured, real-time feed from the shared `activity_logs` collection, grouped by who acted. Updates live as events are written across the Customer app, Professional/Field app and Cloud Functions."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" className="gap-1.5" data-testid="log-change-btn"><Icons.Plus className="h-4 w-4" /> Log a change</Button></DialogTrigger>
            <DialogContent data-testid="log-change-dialog">
              <DialogHeader><DialogTitle className="font-heading">Log a system change</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div className="space-y-1.5"><Label>Change type</Label><Input data-testid="change-type" value={form.change_type} onChange={(e) => setForm({ ...form, change_type: e.target.value })} placeholder="e.g. schema_change, deployment, rules_update" /></div>
                <div className="space-y-1.5"><Label>Description</Label><Textarea data-testid="change-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Related files (comma separated)</Label><Input data-testid="change-files" value={form.related_files} onChange={(e) => setForm({ ...form, related_files: e.target.value })} placeholder="functions/index.js, firestore.rules" /></div>
              </div>
              <DialogFooter><Button onClick={logChange} data-testid="change-submit">Log change</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="activity-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this tab…" className="pl-9" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          {Object.entries(TAB_META).map(([k, m]) => {
            const I = Icons[m.icon] || Icons.Circle;
            return <TabsTrigger key={k} value={k} data-testid={`activity-tab-${k}`} className="gap-1.5"><I className="h-4 w-4" /> {m.label} ({counts[k] || 0})</TabsTrigger>;
          })}
        </TabsList>
      </Tabs>

      <Card className="p-4">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center" data-testid="activity-empty">No {TAB_META[tab].label} activity yet.</p>
        ) : (
          <div className="relative pl-4" data-testid="activity-timeline">
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />
            <div className="space-y-1">
              {rows.map((l, idx) => {
                const I = Icons[l._meta.icon] || Icons.Activity;
                const tone = TAB_META[l._meta.tab].tone;
                return (
                  <div key={`${l.id || "log"}-${idx}`} className="relative flex gap-3 rounded-lg p-2.5 hover:bg-accent/40 transition-colors" data-testid={`activity-row-${l.id}`}>
                    <div className={`relative z-10 -ml-4 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${tone}`}><I className="h-3.5 w-3.5" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{l._meta.label}</span>
                        {l.is_demo && <span className="text-[10px] font-mono text-muted-foreground border border-border rounded px-1">demo</span>}
                        {l.status === "pending" && <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2">pending</span>}
                        {l.status === "approved" && <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2">approved</span>}
                        {l.status === "rejected" && <span className="text-[10px] font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2">rejected</span>}
                      </div>
                      {metaStr(l) && <p className="text-xs text-muted-foreground break-words mt-0.5">{metaStr(l)}</p>}
                      <p className="text-[11px] font-mono text-muted-foreground mt-0.5">{text(l.performed_by || l.changed_by, "system")}{l.performed_by_role ? ` · ${l.performed_by_role}` : ""} · {ago(ts(l))}</p>
                    </div>
                    {l.id && <EditRecordButton path="activity_logs" id={l.id} />}
                    {canApprove && l.status === "pending" && (
                      <div className="flex flex-col gap-1 shrink-0">
                        <Button size="sm" className="h-7 gap-1 bg-emerald-600 hover:bg-emerald-500" onClick={() => review(l, "approved")} data-testid={`approve-${l.id}`}><Icons.Check className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="outline" className="h-7 gap-1 text-red-600 border-red-200" onClick={() => review(l, "rejected")} data-testid={`reject-${l.id}`}><Icons.X className="h-3.5 w-3.5" /></Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
